import { Link } from 'react-router-dom';
import { useLang } from '@/contexts/LanguageContext';
import { Instagram, Twitter, Facebook, Youtube } from 'lucide-react';

const LOGO_URL = '/assets/logo.png';

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.54a8.27 8.27 0 0 0 4.76 1.5V6.69h-1z"/>
  </svg>
);

export default function Footer() {
  const { t, lang } = useLang();

  const quickLinks = [
    { path: '/about', label: t('nav.about') },
    { path: '/book', label: t('nav.book') },
    { path: '/journal', label: t('nav.journal') },
    { path: '/forum', label: t('nav.forum') },
    { path: '/contact', label: t('nav.contact') },
    { path: '/track-order', label: lang === 'ar' ? 'تتبع طلبك' : 'Track Order' },
  ];

  const socialLinks = [
    { icon: Instagram, href: 'https://instagram.com/tariqwithin', label: 'Instagram' },
    { icon: TikTokIcon, href: 'https://tiktok.com/@tariqwithin', label: 'TikTok', custom: true },
    { icon: Twitter, href: 'https://x.com/tariqwithin', label: 'X/Twitter' },
    { icon: Facebook, href: 'https://facebook.com/tariqwithin', label: 'Facebook' },
    { icon: Youtube, href: 'https://youtube.com/@tariqwithin', label: 'YouTube' },
  ];

  return (
    <footer data-testid="main-footer" className="border-t border-sacred-gold/10 mt-auto" style={{
      background: 'rgba(255, 248, 225, 0.6)',
      backdropFilter: 'blur(16px)',
    }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16">
          <div className="space-y-4">
            <img src={LOGO_URL} alt="TariqWithin" className="h-12 w-auto" />
            <p className="text-text-muted text-sm max-w-xs leading-relaxed">
              {lang === 'ar'
                ? '\u0635\u0627\u062d\u0628 \u0631\u0624\u064a\u0629\u060c \u0645\u0624\u0644\u0641\u060c \u0627\u0633\u062a\u0631\u0627\u062a\u064a\u062c\u064a. \u0627\u0644\u062d\u0636\u0648\u0631 \u0627\u0644\u0630\u064a \u064a\u062e\u0644\u0642 \u0627\u0644\u062a\u0623\u062b\u064a\u0631.'
                : 'Visionary, Author, Strategist. The presence that creates influence.'}
            </p>
            <div className="flex items-center gap-4">
              {socialLinks.map(s => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" data-testid={`social-${s.label.toLowerCase()}`} className="text-text-muted hover:text-sacred-gold transition-colors">
                  {s.custom ? <s.icon /> : <s.icon size={20} />}
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-sacred-gold font-body font-semibold text-sm uppercase tracking-wider mb-4">{t('footer.quickLinks')}</h4>
            <nav className="space-y-2">
              {quickLinks.map(link => (
                <Link key={link.path} to={link.path} className="block text-warm-brown hover:text-heading text-sm transition-colors">
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          <div>
            <h4 className="text-sacred-gold font-body font-semibold text-sm uppercase tracking-wider mb-4">{t('contact.title')}</h4>
            <p className="text-warm-brown text-sm mb-2">contact@tariqwithin.com</p>
            <div className="mt-4 space-y-2">
              <Link to="/privacy" className="block text-text-muted hover:text-warm-brown text-xs transition-colors">{t('footer.privacy')}</Link>
              <Link to="/terms" className="block text-text-muted hover:text-warm-brown text-xs transition-colors">{t('footer.terms')}</Link>
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event('tw:open-cookies'))}
                data-testid="footer-cookie-settings"
                className="block text-text-muted hover:text-warm-brown text-xs transition-colors text-left rtl:text-right"
              >
                {t('cookies.manage')}
              </button>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-sacred-gold/8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-text-muted">
          <p>&copy; {t('footer.rights')}</p>
          <p>
            {t('footer.poweredBy')}{' '}
            <a href="https://visualte.com" target="_blank" rel="noopener noreferrer" className="text-sacred-gold/80 hover:text-sacred-gold font-body font-semibold">
              VISUALTE&reg;
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
